import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { fetchUniqueCallerStats } from '@/api/callersApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import { formatDateTime, formatPhoneNumber } from '@/utils/format'

const SEARCH_FIELDS = [
  'caller_name',
  'last_call_on',
  'caller_id',
  'caller_id_display',
  'total_calls',
]

function normalizeCallerRow(raw, idx) {
  const rawCallerId =
    raw.caller_id != null && String(raw.caller_id).trim() !== ''
      ? String(raw.caller_id).trim()
      : ''

  return {
    id: rawCallerId || `caller-${idx}`,
    caller_name: raw.caller_name ?? null,
    last_call_on: raw.last_call_on ?? null,
    // Keep API/raw value for mutations; use display field only in UI
    caller_id: rawCallerId || '—',
    caller_id_display: rawCallerId ? formatPhoneNumber(rawCallerId) : '—',
    total_calls: raw.total_calls ?? null,
    country_code: raw.country_code ?? null,
  }
}

export function displayCallerName(value) {
  const name = String(value ?? '').trim()
  return name || '—'
}

export function useCallersData() {
  const store = useStore()
  const route = useRoute()

  const callers = ref([])
  const loading = ref(false)
  const page = ref(1)
  const itemsPerPage = ref(25)

  const isSessionReady = computed(() => store.getters.isSessionReady)
  const sessionContext = computed(() => store.getters.sessionContext)
  const tableSearch = computed(() => store.state.tableSearch)

  const callerRows = computed(() => {
    const rows = Array.isArray(callers.value) ? callers.value : []
    return rows.map(normalizeCallerRow)
  })

  // KAN-33: the list was never actually sorted -- it just rendered whatever
  // order the backend happened to return (DynamoDB scan order, effectively
  // random from the caller's point of view). Most-recently-called first is
  // the useful default for a callers list; a caller never called has no
  // last_call_on at all, so those sink to the bottom rather than being
  // scattered throughout by an undefined/NaN comparison.
  const sortedCallerRows = computed(() => {
    return [...callerRows.value].sort((a, b) => {
      const aTime = a.last_call_on ? new Date(a.last_call_on).getTime() : 0
      const bTime = b.last_call_on ? new Date(b.last_call_on).getTime() : 0
      return bTime - aTime
    })
  })

  const filteredCallers = computed(() => {
    const q = (tableSearch.value || '').trim().toLowerCase()
    if (!q) return sortedCallerRows.value
    return sortedCallerRows.value.filter((item) =>
      SEARCH_FIELDS.some((field) => {
        if (field === 'last_call_on') {
          return formatDateTime(item.last_call_on).toLowerCase().includes(q)
        }
        return String(item[field] ?? '')
          .toLowerCase()
          .includes(q)
      }),
    )
  })

  const totalCallers = computed(() => filteredCallers.value.length)

  const totalCalls = computed(() =>
    filteredCallers.value.reduce((sum, caller) => {
      const count = Number(caller.total_calls)
      return sum + (Number.isFinite(count) ? count : 0)
    }, 0),
  )

  const pageCount = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return 1
    return Math.max(1, Math.ceil(filteredCallers.value.length / perPage))
  })

  const paginatedCallers = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return filteredCallers.value
    const start = (page.value - 1) * perPage
    return filteredCallers.value.slice(start, start + perPage)
  })

  const rangeLabel = computed(() => {
    const total = filteredCallers.value.length
    if (!total) return '0 of 0'
    const perPage = itemsPerPage.value
    if (perPage <= 0) return `1-${total} of ${total}`
    const start = (page.value - 1) * perPage + 1
    const end = Math.min(page.value * perPage, total)
    return `${start}-${end} of ${total}`
  })

  const isSearchActive = computed(() => Boolean((tableSearch.value || '').trim()))

  async function loadCallers() {
    const { org_id } = sessionContext.value ?? {}
    if (!isSessionReady.value || !org_id) return { ok: false }

    loading.value = true
    try {
      const response = await fetchUniqueCallerStats({ org_id })
      const rows = unwrapApiList(response)

      if (rows.length) {
        callers.value = rows
        page.value = 1
        return { ok: true }
      }

      if (!isApiSuccess(response)) {
        callers.value = []
        return response
      }

      callers.value = rows
      page.value = 1
      return { ok: true }
    } catch (err) {
      callers.value = []
      throw err
    } finally {
      loading.value = false
    }
  }

  function setPage(next) {
    page.value = Math.min(Math.max(1, next), pageCount.value)
  }

  function setItemsPerPage(value) {
    itemsPerPage.value = value
    page.value = 1
  }

  watch(filteredCallers, () => {
    if (page.value > pageCount.value) {
      page.value = pageCount.value
    }
  })

  watch(
    isSessionReady,
    (ready) => {
      if (ready) loadCallers()
    },
    { immediate: true },
  )

  watch(
    () => route.name,
    (name) => {
      if (name === 'Callers' && isSessionReady.value) {
        loadCallers()
      }
    },
  )

  watch(tableSearch, () => {
    page.value = 1
  })

  return {
    loading,
    page,
    itemsPerPage,
    pageCount,
    isSessionReady,
    filteredCallers,
    paginatedCallers,
    totalCallers,
    totalCalls,
    rangeLabel,
    isSearchActive,
    loadCallers,
    setPage,
    setItemsPerPage,
  }
}

export function getCallersErrorMessage(payload) {
  return getApiMessage(payload, 'Failed to load callers')
}
